"use client"

import type { getDictionary } from "@/get-dictionary"
import { useState } from "react"
import sendMail, { sendToSalesForce } from "@/actions/mailActions"
import { Logger } from "next-axiom/dist/logger"
import { useToast } from "@/components/ui/use-toast"
import { useRouter } from "next/navigation"
import type { Affiliation } from "@/affiliations-config"

type Props = {
  dictionary: Awaited<ReturnType<typeof getDictionary>>["internationalPatients"]
  affiliation: Affiliation
  utmMedium: string
  utmSource: string
}

export default function DoctorProfileHighlightsSection({
  dictionary,
  affiliation,
  utmMedium,
  utmSource,
}: Props) {
  const { toast } = useToast()
  const router = useRouter()

  const [email, setEmail] = useState("")
  const [subject, setSubject] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [country, setCountry] = useState("")
  const [message, setMessage] = useState("")
  const [name, setName] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [error, setError] = useState(false)

  const LOG = new Logger({
    source: "ContactUsFormSection",
  })

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const emailBody = `
      <p>===============================================</p>
      <h1>${subject}</h1>
      <h3>From: ${name}</h3>
      <h3>Email: ${email}</h3>
      <h3>WhatsApp Number: ${phoneNumber}</h3>
      <h3>Country: ${country}</h3>
      <p>Message: ${message}</p>
      <h3>UTM source: ${utmSource}</h3>
      <h3>UTM medium: ${utmMedium}</h3>
      <p>===============================================</p>
    `
    if (!name || !phoneNumber || !subject || !message || !country) {
      setError(true)
      setErrorMessage("All fields are required")
    } else {
      try {
        const mailSubject = `${message} - ${country}`
        const success = await sendMail(emailBody, mailSubject)
        if (success) {
          toast({
            variant: "success",
            description:
              "Thank you for reaching out. We will get back to you shortly",
          })
          await sendToSalesForce({
            last_name: name,
            whatsappNum: phoneNumber,
            email,
            country,
            subject: mailSubject,
            pageType: "International Patient",
            pageName: "Enquiry Form",
            source: utmSource,
            medium: utmMedium,
          })
          router.push(
            `/${affiliation}/thankyou?name=${encodeURIComponent(name)}`
          )
        }
      } catch (error) {
        LOG.error("Error in sending mail ", error as { [key: string]: any })
        toast({
          variant: "destructive",
          description: "Something went wrong. Please try again later",
        })
      }
      setError(false)
    }
  }

  return (
    <section className="container grid w-full grid-cols-55-45 grid-rows-3">
      {/* 1st block */}
      <div className="col-span-2 row-span-2 text-wrap bg-brand-500 p-4 text-secondary sm:col-span-1 sm:row-span-3">
        <div className="space-y-8">
          <h3 className="text-3xl font-bold sm:text-5xl">
            {dictionary._enquiry}
          </h3>
          <form
            id="international-patients-contactus-form"
            onSubmit={handleSubmit}
            className="grid grid-cols-2 gap-4"
          >
            <input
              type="text"
              placeholder={dictionary._name}
              className="col-span-2 h-12 flex-grow rounded-xl border p-2 text-black sm:col-span-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              type="text"
              placeholder={dictionary._email}
              className="col-span-2 h-12 flex-grow rounded-xl border p-2 text-black sm:col-span-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="number"
              placeholder={dictionary._whatsapp}
              id="phone"
              className="col-span-2 h-12 w-full rounded-xl border p-2 text-black sm:col-span-1"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <input
              type="text"
              placeholder={dictionary._country}
              id="country"
              className="col-span-2 h-12 w-full rounded-xl border p-2 text-black sm:col-span-1"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
            <input
              type="text"
              placeholder={dictionary._subject}
              id="subject"
              className="col-span-2 h-12 w-full rounded-xl border p-2 text-black"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <textarea
              className="col-span-2 h-96 w-full rounded-xl border p-2 text-black"
              placeholder={dictionary._message}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            ></textarea>
            <div className={"h-4 text-right text-lg"}>
              <p className={`text-lg ${error ? "text-red-600" : ""}`}>
                {errorMessage}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="rounded bg-[#00B06F] px-4 py-2 text-white"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* 2nd block */}
      <div className="col-span-2 row-span-2 flex h-full flex-col items-center gap-6 text-wrap bg-custom-10 p-8 text-secondary sm:col-span-1 sm:row-span-3">
        <h3 className="text-lg font-bold sm:text-4xl">
          International Patient Services at Sterling
        </h3>
        <p className="pt-6 text-xs sm:text-xl">
          Offering assistance with travel arrangements, language interpretation,
          and personalized medical treatment plans
        </p>

        <iframe
          width={"100%"}
          height={"100%"}
          src="https://www.youtube.com/embed/NcAIwJh0jS8?enablejsapi=1&origin=http%3A%2F%2Flocalhost%3A3000&widgetid=3"
        ></iframe>
        {/* <Icon
          name="heroicons--arrow-long-right-solid"
          className="h-8 w-16 self-end"
        /> */}
      </div>
    </section>
  )
}
